import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDeleteQuote, useMe, useQuote, useRefreshQuote, useSubmitQuote } from '../api/hooks';
import { StatusBadge } from '../components/StatusBadge';
import { fmtDate, fmtMoney } from '../lib/format';

export function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const quote = useQuote(id);
  const me = useMe();
  const refresh = useRefreshQuote();
  const submit = useSubmitQuote();
  const del = useDeleteQuote();
  const isApprover = me.data?.user?.role === 'approver';

  if (quote.isLoading) return <p className="muted">Loading…</p>;
  if (quote.error) return <p className="error-text">{(quote.error as Error).message}</p>;
  const q = quote.data;
  if (!q) return null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{q.quoteNumber}</h1>
          <span className="muted">
            {q.customerName}
            {q.creatorFirstName ? ` · by ${q.creatorFirstName}` : ''}
          </span>
        </div>
        <div className="btn-row">
          <StatusBadge status={q.status} />
          {isApprover && q.status === 'Created' && (
            <button className="primary" onClick={() => submit.mutate(q.id)} disabled={submit.isPending}>
              {submit.isPending ? 'Submitting…' : 'Submit to QuickBooks'}
            </button>
          )}
          {q.qboEstimateId && (
            <button onClick={() => refresh.mutate(q.id)} disabled={refresh.isPending}>
              {refresh.isPending ? 'Refreshing…' : 'Refresh status'}
            </button>
          )}
          {q.status !== 'Closed' && (
            <button
              className="link"
              onClick={() => {
                if (window.confirm(`Delete ${q.quoteNumber}?`)) del.mutate(q.id, { onSuccess: () => navigate('/') });
              }}
              disabled={del.isPending}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="row cols-3">
          <div>
            <label>QBO Estimate</label>
            {q.qboEstimateId ?? '—'}
            {q.qboDocNumber ? ` (${q.qboDocNumber})` : ''}
          </div>
          <div>
            <label>Customer email</label>
            {q.customerEmail ?? '—'}
          </div>
          <div>
            <label>Expires</label>
            {fmtDate(q.expirationDate)}
          </div>
        </div>
        {q.invoiceQboId && (
          <div className="banner ok" style={{ marginTop: '1rem' }}>
            Converted to Invoice {q.invoiceQboId} on {fmtDate(q.invoicedAt)}.
          </div>
        )}
      </div>

      <div className="card">
        <h2>Line items</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Supplier</th>
                <th className="right">Qty</th>
                <th className="right">Unit</th>
                <th className="right">Cost</th>
                <th className="right">Amount</th>
                <th className="right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {(q.lineItems ?? []).map((l) => (
                <tr key={l.id}>
                  <td>{l.itemName ?? '—'}</td>
                  <td>{l.supplierName ?? '—'}</td>
                  <td className="right">{l.qty ?? ''}</td>
                  <td className="right">{fmtMoney(l.unitPrice, q.currency)}</td>
                  <td className="right">{fmtMoney(l.unitCost, q.currency)}</td>
                  <td className="right">{fmtMoney(l.amount, q.currency)}</td>
                  <td className="right" style={{ color: (l.margin ?? 0) >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
                    {fmtMoney(l.margin, q.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="right" style={{ marginTop: '0.75rem', lineHeight: 1.8 }}>
          <div className="muted">
            Subtotal {fmtMoney(q.subtotal, q.currency)} · Tax {fmtMoney(q.taxTotal, q.currency)} · Total cost {fmtMoney(q.totalCost, q.currency)}
          </div>
          <div>
            <strong>Total {fmtMoney(q.totalAmount, q.currency)}</strong> ·{' '}
            <span style={{ color: q.totalMargin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
              Margin {fmtMoney(q.totalMargin, q.currency)}
            </span>
          </div>
        </div>
      </div>

      <Link to="/">← Back to quotes</Link>
    </>
  );
}
